import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Modal, ModalDialogue, theme } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { useCancelOrder } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Order cancellation modal with built-in mutation handler
 *
 * Should be used together with the `useOrderCancellationModal` hook.
 *
 * @example
 *
 * import { OrderCancellationModal, useOrderCancellationModal } from '@order/components';
 *
 * const {
 *   isOrderCancellationModalVisible,
 *   showOrderCancellationModal,
 *   hideOrderCancellationModal,
 * } = useOrderCancellationModal();
 *
 * return (
 *   <>
 *    <Button onPress={showOrderCancellationModal}>Cancel Order</Button>
 *    <OrderCancellationModal isVisible={isOrderCancellationModalVisible} onRequestClose={hideOrderCancellationModal} ... />
 *   </>
 * )
 */
export const OrderCancellationModal = (props: OrderCancellationModalProps) => {
  const { isVisible, orderId, restaurantId, onSuccess, onRequestClose } = props;

  const { t } = useLocalizationContext();
  const { track } = useTelemetry();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleSuccess = useCallback(() => {
    onSuccess?.(orderId);
    onRequestClose?.();
  }, [onRequestClose, onSuccess, orderId]);

  const { isCancellingOrder, cancelOrder } = useCancelOrder({
    orderId,
    restaurantId,
    onSuccess: handleSuccess,
    onError: onRequestClose,
  });

  const confirmOrderCancellation = useCallback(() => {
    track('order_cancel.confirmed');

    void cancelOrder();
  }, [cancelOrder, track]);

  const closeModal = useCallback(() => {
    if (isCancellingOrder) return;

    onRequestClose();
  }, [isCancellingOrder, onRequestClose]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      visible={isVisible}
      size="small"
      headline={t('order-cancellation.modal.heading')}
      body={t('order-cancellation.modal.body')}
      accessibilityLabel={t('order-cancellation.modal.a11y-label')}
      onRequestClose={closeModal}
    >
      <Modal.Footer withoutTopBorder style={styles.footer}>
        <View style={styles.ctaContainer}>
          <Button
            size="large"
            palette="secondary"
            onPress={closeModal}
            disabled={isCancellingOrder}
            aria-label={t('order-cancellation.modal.cta.go-back')}
          >
            {t('order-cancellation.modal.cta.go-back')}
          </Button>
        </View>

        <View style={styles.ctaContainer}>
          <Button
            size="large"
            palette="caution"
            onPress={confirmOrderCancellation}
            isLoading={isCancellingOrder}
            aria-label={t('order-cancellation.modal.cta.cancel-order')}
          >
            {t('order-cancellation.modal.cta.cancel-order')}
          </Button>
        </View>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
  },
  ctaContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderCancellationModalProps = Readonly<{
  orderId: string;
  restaurantId: string;
  isVisible: boolean;
  onSuccess?: (orderId: string) => void;
  onRequestClose: () => void;
}>;
